import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomChartBreakdownItem, CustomChartValueType } from '@/entities/dashboards';
import { cn, preventShiftSelection } from '@/shared/lib';
import type { IndicatorSelectHandler } from '../../../model/useIndicatorTransitions';
import { EmptyState } from '../../parts/EmptyState';
import { StatBox } from '../../parts/StatBox';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS } from '../../utils';
import { chartDatePrecision, shownChartValue } from '../chartValues';
import { ChartCardShell } from '../ChartCardShell';

type Props = {
  title: string;
  items: CustomChartBreakdownItem[];
  /** Тип величины категории: по нему величина-дата показывается датой, а число — числом */
  valueType: CustomChartValueType;
  /** Переход к диалогам категории; не задан — блоки нажатий не принимают и нажимаемыми не показаны */
  onSelect?: IndicatorSelectHandler;
};

/**
 * Показатель с переходом к выборке: категории показаны отдельными блоками с названием и величиной —
 * как «Зоны качества» и «Красные зоны» стандартного дашборда.
 *
 * Полос сравнения величин у этого типа нет: сравнение — дело столбчатой диаграммы, а этот тип
 * выбирают ради перехода к диалогам, и блок читается как то, что можно нажать. Блок, за которым
 * диалогов нет, разбирать нечего — он нажимаемым не показан.
 *
 * Число категорий заранее не известно, а доля ширины у диаграммы любая, поэтому блоки встают в
 * колонки по ширине самой диаграммы, а не по ширине экрана.
 */
export const SelectionBreakdownChartView: FC<Props> = ({ title, items, valueType, onSelect }) => {
  const { t } = useTranslation('qualityDashboard');

  // Точность времени берётся по величинам всех категорий сразу — соседние блоки написаны одинаково.
  const precision = chartDatePrecision(
    items.map((item) => item.value),
    valueType,
  );

  return (
    <ChartCardShell title={title}>
      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="@container">
          <div className="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3">
            {items.map((item, index) => {
              const isClickable = !!onSelect && item.conversationIds.length > 0;
              const block = (
                <StatBox
                  label={item.label}
                  value={shownChartValue(item.value, valueType, precision)}
                  className={cn('h-full bg-card', isClickable && DASHBOARD_CLICKABLE_BLOCK_CLASS)}
                />
              );

              // Ключ по месту в перечне: подписи категорий в пределах одной диаграммы могут совпасть.
              if (!isClickable) return <div key={index}>{block}</div>;

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={-1}
                  aria-label={t('presets.chart.openDialogs')}
                  onMouseDown={preventShiftSelection}
                  onClick={(event) => onSelect?.(item, event.shiftKey)}
                >
                  {block}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </ChartCardShell>
  );
};
